import React, { useState, useContext } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Paper, Tabs, Tab, Typography, IconButton, Select, MenuItem, Alert, Button, Box, Chip } from '@mui/material';
import { ArrowBack, Visibility, VisibilityOff } from "@mui/icons-material";
import Grid from '@mui/material/Grid2';
import AppContext from '../components/AppContext.js';
import config from '../config.js';
import Saldo from '../components/common/Saldo';
import MovimentsListEdit from '../components/common/MovimentsListEdit';
import { listAnys, listMesos } from '../functions/common-lists.js';
import { SelectConcepte, fetchListConceptes } from '../components/common/SelectConcepte.js';
import DataLoader from '../components/common/DataLoader.js';
import useBack from '../hooks/useBack.js';
import { ConcepteGraphBanner, SubElementsList } from '../components/common/ConcepteGraphBanner.js';
import { resolveConcepteName, isNodeId, findNodeById, getResumMensualNode } from '../services/concepteGraphService.js';

const DashboardConcepteMensual = () => {
  const { conceptesList: ctxConceptesList, conceptesTree: ctxConceptesTree } = useContext(AppContext);
  const [listConceptes, setListConceptes] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [showDescendants, setShowDescendants] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const branchId1 = searchParams.get('id1');
  const branchId2 = searchParams.get('id2');
  const branchId3 = searchParams.get('id3');

  const [reloadId, setReloadId] = useState(0);
  const { 
    userapp: paramUserapp, 
    anyo: paramAnyo, 
    mes: paramMes, 
    concepte: paramConcepte,
    nodeId: paramNodeId } = useParams();

  const userapp = paramUserapp;
  const anyo = paramAnyo;
  const mes = paramMes;

  const activeNodeId = paramNodeId || (isNodeId(paramConcepte) ? paramConcepte : null);
  const concepte = paramNodeId ? null : (isNodeId(paramConcepte) ? null : paramConcepte);
  const branchKey = `${branchId1 || ''}-${branchId2 || ''}-${branchId3 || ''}`;
  const id = anyo + '-' + mes + '-' + (activeNodeId ? `node-${activeNodeId}-${branchKey}` : concepte) + '-' + reloadId;

  const { onBack } = useBack();

  const gotoBack = (anio, cNom) => {
    onBack();
  }; 

  const handleConcepteChange = (anio, targetMes, selectedConcepte, selectedNode) => {
    // Si no hi ha cap concepte seleccionat (com en prémer la "X"), no navegar ni fer res
    if (!selectedNode && (!selectedConcepte || String(selectedConcepte).trim() === '')) {
      return;
    }

    if (selectedNode?.id && selectedNode.id > 0) {
      const branchParams = new URLSearchParams();
      if (selectedNode.id1) branchParams.set('id1', selectedNode.id1);
      if (selectedNode.id2) branchParams.set('id2', selectedNode.id2);
      if (selectedNode.id3) branchParams.set('id3', selectedNode.id3);
      const branchQuery = branchParams.toString() ? `?${branchParams.toString()}` : '';
      navigate(`/${userapp}/dashboard/${anio}/mes/${targetMes}/node/${selectedNode.id}${branchQuery}`, { replace: true });
    } else {
      navigate(`/${userapp}/dashboard/${anio}/mes/${targetMes}/concepte/${selectedConcepte}`, { replace: true });
    }
  };  

  const fetchFunction = (jwtToken, onData, onError, onFinally) => {
    const allConceptes = listConceptes || ctxConceptesList || [];

    const buildEmptyData = (targetName, nodeObj = null) => ({
      concepte: { nom: targetName, tipus: nodeObj?.tipusFlux || 'DESPESA' },
      node: nodeObj,
      anio: parseInt(anyo, 10),
      mes: parseInt(mes, 10),
      agregat: {
        importAgregat: { saldo: 0, ingressos: 0, despeses: 0, mitjaSaldo: 0, mitjaIngressos: 0, mitjaDespeses: 0 },
        variacioAnyAnterior: { saldo: 0, ingressos: 0, despeses: 0 }
      },
      listConceptesMoviment: [],
      senseDades: true
    });

    // MODE 1: NAVEGACIÓ PER ID NOU DE NODE DE GRAF
    if (activeNodeId) {
      const targetNodeIdNum = parseInt(String(activeNodeId).replace('node:', ''), 10);
      const treeNode = (ctxConceptesTree && targetNodeIdNum) ? findNodeById(ctxConceptesTree, targetNodeIdNum) : null;
      const id1 = branchId1 ? parseInt(branchId1, 10) : (treeNode?.id1 || null);
      const id2 = branchId2 ? parseInt(branchId2, 10) : (treeNode?.id2 || null);
      const id3 = branchId3 ? parseInt(branchId3, 10) : (treeNode?.id3 || null);
      const branchIds = { id1, id2, id3 };

      getResumMensualNode(jwtToken, userapp, targetNodeIdNum, anyo, mes, branchIds, true)
        .then((resData) => {
          if (!resData) {
            onData({ data: buildEmptyData(`Node #${targetNodeIdNum}`) });
            return;
          }
          const rawNode = resData.node;
          const resolvedTreeNode = treeNode || ((ctxConceptesTree && targetNodeIdNum) ? findNodeById(ctxConceptesTree, targetNodeIdNum) : null);
          const node = {
            ...resolvedTreeNode,
            ...rawNode,
            fills: (resolvedTreeNode?.fills && resolvedTreeNode.fills.length > 0) ? resolvedTreeNode.fills : (rawNode?.fills || [])
          };
          const allMovs = resData.listConceptesMoviment || [];
          const agregat = resData.agregat || {
            importAgregat: { saldo: 0, ingressos: 0, despeses: 0, mitjaSaldo: 0, mitjaIngressos: 0, mitjaDespeses: 0 },
            variacioAnyAnterior: { saldo: 0, ingressos: 0, despeses: 0 }
          };

          onData({
            data: {
              concepte: { nom: node?.nom || `Node #${targetNodeIdNum}`, tipus: node?.tipusFlux || 'DESPESA' },
              node: node,
              activeNodeId: targetNodeIdNum,
              conceptePath: node?.conceptePath || node?.path || '',
              anio: parseInt(anyo, 10),
              mes: parseInt(mes, 10),
              agregat: agregat,
              listConceptesMoviment: allMovs,
              senseDades: allMovs.length === 0 && (!agregat.importAgregat || (agregat.importAgregat.despeses === 0 && agregat.importAgregat.ingressos === 0))
            }
          });
        })
        .catch((err) => {
          console.error('Error carregant resum mensual del node:', err);
          onError(err, 'cargar datos del nodo');
        })
        .finally(() => onFinally());

      return;
    }

    // MODE 2: NAVEGACIÓ PER ID ANTIC (CONCEPTE CLÀSSIC)
    const resolvedName = resolveConcepteName(concepte, allConceptes, ctxConceptesTree) || concepte;

    axios.get(`${config.apiBaseUrl}/v1/resummensualconcepte/${userapp}/${anyo}/${mes}/${resolvedName}`, {
      headers: { Authorization: `${jwtToken}`}
    })
    .then((response) => {
      onData(response);
    })
    .catch((err) => {
      if (resolvedName !== resolvedName.toUpperCase()) {
        axios.get(`${config.apiBaseUrl}/v1/resummensualconcepte/${userapp}/${anyo}/${mes}/${resolvedName.toUpperCase()}`, {
          headers: { Authorization: `${jwtToken}`}
        })
        .then((response) => {
          onData(response);
        })
        .catch(() => {
          onData({ data: buildEmptyData(concepte) });
        });
      } else if (err.response?.status === 404 || err.response?.status === 400 || err.response?.status === 500) {
        onData({ data: buildEmptyData(resolvedName) });
      } else {
        onError(err, 'cargar datos');
      }
    })
    .finally(() => onFinally());

    fetchListConceptes(jwtToken, 
      userapp,
      (data)=>{
        setListConceptes(data);
      },
      (err)=>{
        onError(err, 'cargar conceptes');
      }
    );
  };

  return (
    <DataLoader id={id} fetchFunction={fetchFunction} goBackLocation={`/${userapp}/dashboard/${anyo}/mes/${mes}`}>
      {(data, onError) => {
        const isInversio = data.concepte?.tipus === 'INVERSIO';
        return (
        <>
          <Grid 
            size={{ xs: 12 }} 
            sx={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '10px', flexWrap: 'wrap' }}
          >
            <IconButton onClick={() => gotoBack(data.anio, data.concepte.nom)}>
              <ArrowBack />
            </IconButton>

            <Typography variant="h5" sx={{ whiteSpace: 'nowrap' }}>
              Dashboard Mensual Concepte
            </Typography>

            <Select
              value={data.anio}
              size="small"
              onChange={(e) => handleConcepteChange(e.target.value, data.mes, data.concepte.nom, data.node)}
              sx={{ minWidth: 90 }}
            >
              {listAnys().map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>

            <Select
              value={data.mes}
              size="small"
              onChange={(e) => handleConcepteChange(data.anio, e.target.value, data.concepte.nom, data.node)}
              sx={{ minWidth: 110 }}
            >
              {listMesos.map((m) => (
                <MenuItem key={m.key} value={m.key}>
                  {m.name}
                </MenuItem>
              ))}
            </Select>
            
            <SelectConcepte
              label="Concepte"
              value={data.conceptePath || (data.activeNodeId ? `node:${data.activeNodeId}` : data.concepte.nom)}
              onChange={(e, node) => handleConcepteChange(data.anio, data.mes, e.target.value, node || e.target?.node)}
              listConceptes={listConceptes} 
              sx={{ width: { xs: '100%', sm: 240 }, maxWidth: 280 }}
            />        
          </Grid>

          {data.senseDades && (
            <Alert
              severity="info"
              sx={{ mt: 2, mb: 1 }}
              action={
                <Button color="inherit" size="small" onClick={() => gotoBack(data.anio, data.concepte.nom)}>
                  Tornar al Dashboard
                </Button>
              }
            >
              El concepte <strong>{data.node?.pathLabel || data.node?.nom || data.concepte.nom}</strong> encara no té dades ni moviments associats per a aquest mes.
            </Alert>
          )}

          <Grid container spacing={2} sx={{ marginTop: '10px' }}>
            <Saldo titol="Saldo Concepte Mensual" 
              saldo={data.agregat.importAgregat.saldo} 
              ingressos={data.agregat.importAgregat.ingressos}
              despeses={data.agregat.importAgregat.despeses}
              size={{xs: 12, sm: 6, md: 6 }}
              mode={isInversio ? 'inversiones' : 'actual'}
            />

            <Saldo titol="Dif. any anterior" 
              saldo={data.agregat.variacioAnyAnterior?.saldo || 0} 
              ingressos={data.agregat.variacioAnyAnterior?.ingressos || 0}
              despeses={data.agregat.variacioAnyAnterior?.despeses || 0} 
              mode={isInversio ? 'inversiones' : 'actual'}
            />

            <Saldo titol="Mitja Diari" 
              saldo={data.agregat.importAgregat.mitjaSaldo} 
              ingressos={data.agregat.importAgregat.mitjaIngressos}
              despeses={data.agregat.importAgregat.mitjaDespeses} 
              mode={isInversio ? 'inversiones' : 'actual'}
            />

          </Grid>

          <ConcepteGraphBanner 
            concepteNom={data.concepte.nom} 
            userapp={userapp} 
            anyo={data.anio}
            mes={data.mes} 
          />

          <Grid container spacing={2} sx={{ marginTop: '10px' }}>
            <Grid size={12}>
            {(() => {
              const hasChildren = Boolean(data.node?.fills && data.node.fills.length > 0);
              const allMovs = data.listConceptesMoviment || [];
              const directMovs = allMovs.filter(m => {
                if (!data.node) return true;
                const targetId = (typeof m.nodeFulla === 'number' ? m.nodeFulla : m.nodeFulla?.id) || m.id_node_fulla || m.idNodeFulla;
                if (targetId !== undefined && targetId !== null) {
                  return Number(targetId) === Number(data.node.id);
                }
                const targetPath = (m.conceptePath || (typeof m.nodeFulla === 'object' ? m.nodeFulla?.conceptePath : '') || '').trim().toUpperCase();
                const nodePath = (data.node.conceptePath || data.node.path || '').trim().toUpperCase();
                if (targetPath && nodePath) {
                  return targetPath === nodePath;
                }
                return true;
              });

              const movsToShow = (!hasChildren || showDescendants) ? allMovs : directMovs;

              return (
                <Paper>
                  <Tabs value={tabValue} onChange={(e, val) => setTabValue(val)} indicatorColor="primary" textColor="primary" centered>
                    <Tab label={`Moviments (${movsToShow.length})`} />
                    <Tab label="Subelements / Branca" />
                  </Tabs>
                  {tabValue === 0 && (
                    <Box sx={{ p: 2 }}>
                      {hasChildren && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 1.5,
                            p: 1.5,
                            mb: 2,
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            border: '1px solid #e0e0e0'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                              Filtre de moviments:
                            </Typography>
                            <Chip
                              label={`Tots (amb fills: ${allMovs.length})`}
                              color={showDescendants ? 'primary' : 'default'}
                              variant={showDescendants ? 'filled' : 'outlined'}
                              onClick={() => setShowDescendants(true)}
                              size="small"
                              clickable
                            />
                            <Chip
                              label={`Només node pare (${directMovs.length})`}
                              color={!showDescendants ? 'primary' : 'default'}
                              variant={!showDescendants ? 'filled' : 'outlined'}
                              onClick={() => setShowDescendants(false)}
                              size="small"
                              clickable
                            />
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={showDescendants ? <VisibilityOff /> : <Visibility />}
                            onClick={() => setShowDescendants(prev => !prev)}
                            sx={{ textTransform: 'none' }}
                          >
                            {showDescendants ? 'Ocultar fills' : 'Mostrar fills'}
                          </Button>
                        </Box>
                      )}
                      <MovimentsListEdit 
                        moviments={movsToShow} 
                        onError={onError} 
                        onEditComplete={() => setReloadId(prev => prev + 1)}
                      />
                    </Box>
                  )}
                  {tabValue === 1 && (
                    <SubElementsList
                      concepteNom={data.concepte.nom}
                      nodeId={data.activeNodeId}
                      activeNode={data.node}
                      path={data.conceptePath}
                      userapp={userapp}
                      anyo={data.anio}
                      mes={data.mes}
                    />
                  )}
                </Paper>
              );
            })()}
            </Grid>
          </Grid>
      </>
      );
    }}
    </DataLoader>
  );
};

export default DashboardConcepteMensual;
